import { Link } from "react-router-dom";
import { Coins, Wallet, Bot } from "lucide-react";

export const fmtCryptoPrice = (p) => (p == null ? "—" : p < 0.01 ? `$${Number(p).toPrecision(3)}`
  : `$${Number(p).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: p < 1 ? 4 : 2 })}`);
const usd = (n) => `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qtyFmt = (q) => (q >= 1000 ? q.toLocaleString(undefined, { maximumFractionDigits: 0 }) : q.toLocaleString(undefined, { maximumFractionDigits: 6 }));

export const SourceTag = ({ source }) => (source === "woodrow"
  ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border border-bt-gold/40 text-bt-gold bg-bt-gold/10"><Bot className="w-3 h-3" />Woodrow</span>
  : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border border-bt-border-subtle text-bt-text-secondary bg-bt-surface-2"><Wallet className="w-3 h-3" />My Picks</span>);

const HoldingRow = ({ h, image }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-t border-bt-border-subtle" data-testid={`crypto-holding-${h.symbol}-${h.source}`}>
    {image ? <img src={image} alt={h.name} className="w-7 h-7 rounded-full" /> : <Coins className="w-7 h-7 text-bt-gold" />}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-sm font-medium text-bt-text-primary">{h.symbol}</span>
        <SourceTag source={h.source} />
        {h.short && <span className="text-[10px] font-mono text-bt-negative">SHORT</span>}
      </div>
      <p className="text-xs font-mono text-bt-text-tertiary">{qtyFmt(h.quantity)} @ {fmtCryptoPrice(h.avg_price)} · now {fmtCryptoPrice(h.price)}</p>
    </div>
    <div className="text-right shrink-0">
      <p className="font-mono text-sm text-bt-text-primary">{usd(h.value)}</p>
      <p className={`font-mono text-xs ${h.pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
        {h.pnl >= 0 ? "+" : ""}{usd(h.pnl)} ({h.pnl_pct >= 0 ? "+" : ""}{h.pnl_pct}%)
      </p>
    </div>
  </div>
);

export default function CryptoHoldingsPanel({ data, images }) {
  if (!data) return null;
  const { holdings, total_value, total_pnl } = data;
  return (
    <div className="bg-bt-surface border border-bt-gold/30 mb-6" data-testid="crypto-holdings-panel">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Your Crypto Holdings</h2>
        </div>
        {holdings.length > 0 && (
          <div className="text-right" data-testid="crypto-holdings-total">
            <span className="font-mono text-sm text-bt-text-primary">{usd(total_value)}</span>
            <span className={`ml-2 font-mono text-xs ${total_pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>{total_pnl >= 0 ? "+" : ""}{usd(total_pnl)}</span>
          </div>
        )}
      </div>
      {holdings.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-bt-text-tertiary" data-testid="crypto-holdings-empty">
          You don't hold any crypto yet. Add a coin from My Picks, or raise Woodrow's Crypto weight in Strategy Settings.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {holdings.map(h => <HoldingRow key={`${h.symbol}-${h.source}`} h={h} image={h.image || images[h.symbol.toLowerCase()]} />)}
        </div>
      )}
      {data.other_woodrow_holdings?.length > 0 && (
        <p className="px-4 py-2 border-t border-bt-border-subtle text-xs font-mono text-bt-text-tertiary" data-testid="crypto-holdings-other-note">
          +{data.other_woodrow_holdings.length} non-crypto holding{data.other_woodrow_holdings.length > 1 ? "s" : ""} ({data.other_woodrow_holdings.join(", ")}) —{" "}
          <Link to="/woodrow" className="text-bt-gold hover:underline" data-testid="crypto-holdings-other-link">view in Woodrow</Link>
        </p>
      )}
    </div>
  );
}
